import React, { useState, useMemo, useCallback } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// SORTING FOR THE PARTNER-SECTION TABLES.
//
// Jesse: "i want to be able to sort the Partners Leads, Partners, and accounts
// page tables."
//
// 🔑 ONE HOOK, NOT THREE IMPLEMENTATIONS — the lesson from the row drawers in
// the same brief, applied before rather than after. Four pages had four ways of
// opening a row because each solved it alone; three pages sorting three ways
// would be the same story with a different noun.
//
// ⚠️ THE COMPARATOR IS TYPE-AWARE, because these columns are not all strings.
// A plain localeCompare puts "10" before "9" on a referral count and sorts
// "Jan 2" above "Feb 1" on a date. The accessor says what a column IS and the
// comparator follows it.
//
// ⚠️ EMPTY SORTS LAST IN BOTH DIRECTIONS, which is deliberate and is not what a
// naive comparator does. A partner with no closing date is not "earliest"; they
// are unknown, and flipping the direction should not parade unknowns to the top.
// ─────────────────────────────────────────────────────────────────────────────

const isBlank = (v) => v === null || v === undefined || v === '';

/**
 * @param {object} columns  key → accessor. An accessor is either a function
 *                          (row) => value, or { get, type } where type is
 *                          'number' | 'date' | 'text' (default 'text').
 * @param {string} initialKey
 * @param {'asc'|'desc'} initialDir
 */
export function useTableSort(columns, initialKey = null, initialDir = 'asc') {
  // ⚠️ ONE STATE OBJECT, NOT TWO. The first version held key and dir separately
  // and decided "is this the third click" by setting a flag inside one updater
  // and reading it right after calling the other — which React is free to batch
  // or replay, so the clear would fire late or twice. Key and direction change
  // together and are therefore one value.
  const [sort, setSort] = useState({ key: initialKey, dir: initialDir });
  const { key: sortKey, dir: sortDir } = sort;

  // ⚠️ A NEW COLUMN STARTS ASCENDING, and only a repeat click flips. Carrying
  // the previous column's direction over makes the first click on a new column
  // land on an order nobody asked for.
  //
  // 🔑 AND THE THIRD CLICK CLEARS IT, which AdminREPartners' own sort already
  // did with a reason worth keeping: "returning to the server's order has to be
  // reachable without a page reload." That order is not arbitrary — it is the
  // ranking the endpoint chose — and a control that can only ever reorder is one
  // that destroys it on the first click.
  const toggle = useCallback((key) => {
    setSort((prev) => {
      if (prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return { key: null, dir: 'asc' };
    });
  }, []);

  const sortRows = useCallback((rows) => {
    if (!sortKey || !Array.isArray(rows)) return rows;
    const spec = columns[sortKey];
    if (!spec) return rows;
    const get = typeof spec === 'function' ? spec : spec.get;
    const type = typeof spec === 'function' ? 'text' : (spec.type || 'text');
    const dir = sortDir === 'asc' ? 1 : -1;

    // 🔑 A COPY, AND A STABLE TIE-BREAK ON THE ORIGINAL INDEX. Array.prototype
    // .sort is stable in modern engines, but these lists are re-sorted from a
    // memo on every render — without a fixed tie-break, rows with equal values
    // can swap places as unrelated state changes, which reads as the table
    // twitching.
    return rows
      .map((row, i) => ({ row, i }))
      .sort((a, b) => {
        const av = get(a.row);
        const bv = get(b.row);
        // ⚠️ BLANKS LAST REGARDLESS OF DIRECTION — see the header.
        if (isBlank(av) && isBlank(bv)) return a.i - b.i;
        if (isBlank(av)) return 1;
        if (isBlank(bv)) return -1;
        let cmp;
        if (type === 'number') cmp = (Number(av) || 0) - (Number(bv) || 0);
        else if (type === 'date') cmp = new Date(av).getTime() - new Date(bv).getTime();
        else cmp = String(av).localeCompare(String(bv), undefined, { sensitivity: 'base', numeric: true });
        return cmp !== 0 ? cmp * dir : a.i - b.i;
      })
      .map((x) => x.row);
  }, [columns, sortKey, sortDir]);

  // The header cell. Renders the column label with its state, and is a real
  // button so it is reachable by keyboard and announced as one.
  const SortHeader = useCallback(({ column, children, align = 'left', style = {} }) => {
    const active = sortKey === column;
    return (
      <button
        type="button"
        onClick={() => toggle(column)}
        aria-label={`Sort by ${typeof children === 'string' ? children : column}`}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          background: 'none', border: 'none', padding: 0, cursor: 'pointer',
          font: 'inherit', color: 'inherit', textTransform: 'inherit',
          letterSpacing: 'inherit', justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
          ...style,
        }}
      >
        {children}
        {/* ⚠️ THE INACTIVE ARROW IS SHOWN, GREYED, NOT HIDDEN. A sort control
            that only appears once used is a sort control nobody discovers. */}
        <span style={{ color: active ? '#3a6ad9' : '#d1d5db', fontSize: 11 }}>
          {active ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}
        </span>
      </button>
    );
  }, [sortKey, sortDir, toggle]);

  return { sortKey, sortDir, toggle, sortRows, SortHeader };
}

/** Convenience: memoised sorted rows. */
export function useSortedRows(rows, sortRows) {
  return useMemo(() => sortRows(rows), [rows, sortRows]);
}
